import React, { useEffect, useState, useCallback } from 'react';
import { Tag, Plus, Trash2, ToggleLeft, ToggleRight, X, Loader2, Check } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * Tab admin "Tarifs Pro" — gestion des remises par type d'organisation
 * (étudiant, labo, fournisseur...). Chaque organization_type peut avoir
 * exactement 1 tarif. Discount entre 0% et 50%.
 */
const ORG_TYPES = [
  { value: 'general_dentistry', label: 'Cabinet dentaire' },
  { value: 'lab',               label: 'Laboratoire' },
  { value: 'oral_surgery',      label: 'Chirurgie buccale' },
  { value: 'maxillofacial',     label: 'Chirurgie maxillo-faciale' },
  { value: 'orthodontics',      label: 'Orthodontie' },
  { value: 'hygienist',         label: 'Hygiéniste / Prophylaxie' },
  { value: 'student',           label: 'Étudiant' },
  { value: 'other',             label: 'Autre' },
];

const PriceTiersTab = () => {
  const { toast } = useToast();
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ organization_type: 'student', label: 'Tarif Étudiant', discount_percent: 10 });

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/price-tiers');
      setTiers(Array.isArray(r.data) ? r.data : []);
    } catch {
      toast({ title: 'Erreur', description: 'Impossible de charger les tarifs.' });
    } finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    if (!form.label.trim()) { toast({ title: 'Label requis' }); return; }
    setBusy(true);
    try {
      await api.post('/admin/price-tiers', {
        organization_type: form.organization_type,
        label: form.label.trim(),
        discount_percent: Number(form.discount_percent),
      });
      toast({ title: '✅ Tarif créé' });
      setCreating(false);
      setForm({ organization_type: 'student', label: 'Tarif Étudiant', discount_percent: 10 });
      await reload();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Création impossible' });
    } finally { setBusy(false); }
  };

  const toggle = async (t) => {
    try {
      await api.patch(`/admin/price-tiers/${t.id}`, { active: !t.active });
      setTiers(prev => prev.map(x => x.id === t.id ? { ...x, active: !x.active } : x));
    } catch { toast({ title: 'Erreur' }); }
  };

  const updateDiscount = async (t, value) => {
    const v = Math.max(0, Math.min(50, Number(value) || 0));
    try {
      await api.patch(`/admin/price-tiers/${t.id}`, { discount_percent: v });
      setTiers(prev => prev.map(x => x.id === t.id ? { ...x, discount_percent: v } : x));
    } catch { toast({ title: 'Erreur' }); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer le tarif "${t.label}" ?`)) return;
    try {
      await api.delete(`/admin/price-tiers/${t.id}`);
      toast({ title: 'Supprimé' });
      await reload();
    } catch { toast({ title: 'Erreur' }); }
  };

  const usedTypes = new Set(tiers.map(t => t.organization_type));
  const availableTypes = ORG_TYPES.filter(t => !usedTypes.has(t.value));

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-[#7A6F5C]" /></div>;

  return (
    <div data-testid="admin-price-tiers-tab" className="space-y-5">
      <div className="bg-white border border-[#E8E0D0] rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-medium text-[#2D2A26] flex items-center gap-2"><Tag className="w-4 h-4 text-[#1F4D3F]" /> Tarifs Pro par segment</h3>
          <p className="text-xs text-[#7A6F5C] mt-1 max-w-2xl">
            Définissez une remise automatique par type d'organisation. Ex : <strong>-10% étudiants</strong>, <strong>-15% laboratoires</strong>.
            Appliquée à tous les acheteurs dont le profil correspond, sur l'ensemble du catalogue.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}
          data-testid="price-tier-create-btn"
          disabled={availableTypes.length === 0}
          className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">
          <Plus className="w-4 h-4 mr-1.5" /> Nouveau tarif
        </Button>
      </div>

      {tiers.length === 0 ? (
        <div className="bg-white border border-dashed border-[#E8E0D0] rounded-2xl p-10 text-center" data-testid="price-tiers-empty">
          <Tag className="w-10 h-10 text-[#C9BFAE] mx-auto mb-3" />
          <p className="text-sm text-[#7A6F5C]">Aucun tarif configuré. Créez-en un pour offrir des remises ciblées.</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden" data-testid="price-tiers-list">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] text-left text-[#7A6F5C] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Segment</th>
                <th className="px-4 py-3">Label affiché</th>
                <th className="px-4 py-3">Remise</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map(t => {
                const meta = ORG_TYPES.find(o => o.value === t.organization_type);
                return (
                  <tr key={t.id} className="border-t border-[#F5F1EA]" data-testid={`price-tier-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#2D2A26]">{meta?.label || t.organization_type}</div>
                      <div className="text-[10px] text-[#7A6F5C] font-mono mt-0.5">{t.organization_type}</div>
                    </td>
                    <td className="px-4 py-3 text-[#5A5247]">{t.label}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Input type="number" min="0" max="50" step="1"
                          defaultValue={t.discount_percent}
                          onBlur={(e) => updateDiscount(t, e.target.value)}
                          data-testid={`price-tier-discount-${t.id}`}
                          className="h-8 w-20 text-center" />
                        <span className="text-[#7A6F5C]">%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => toggle(t)}
                        data-testid={`price-tier-toggle-${t.id}`}
                        title={t.active ? 'Désactiver' : 'Activer'}
                        className={`mr-1 ${t.active ? 'text-emerald-600' : 'text-[#C9BFAE]'}`}>
                        {t.active ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                      </button>
                      <button onClick={() => remove(t)}
                        data-testid={`price-tier-delete-${t.id}`}
                        className="p-1.5 hover:bg-rose-50 rounded text-rose-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start sm:items-center justify-center overflow-y-auto py-6 px-4" data-testid="price-tier-create-modal">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E8E0D0]">
              <h3 className="font-medium text-[#2D2A26]">Nouveau tarif Pro</h3>
              <button onClick={() => setCreating(false)} className="text-[#7A6F5C] hover:text-[#2D2A26]"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Segment cible *</label>
                <select value={form.organization_type}
                  onChange={e => setForm({ ...form, organization_type: e.target.value, label: `Tarif ${ORG_TYPES.find(o => o.value === e.target.value)?.label}` })}
                  data-testid="price-tier-form-type"
                  className="w-full h-11 px-3 rounded-md border border-[#E8E0D0] bg-white text-base focus:outline-none focus:ring-2 focus:ring-[#1F4D3F]">
                  {availableTypes.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="text-[11px] text-[#7A6F5C] mt-1">Un segment ne peut avoir qu'un seul tarif. Désactivez ou modifiez l'existant pour le réutiliser.</p>
              </div>
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Label affiché *</label>
                <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })}
                  placeholder="Ex: Tarif Étudiant"
                  data-testid="price-tier-form-label" />
                <p className="text-[11px] text-[#7A6F5C] mt-1">Texte visible sur le badge produit côté acheteur.</p>
              </div>
              <div>
                <label className="text-sm text-[#2D2A26] mb-1.5 block">Remise (%) *</label>
                <Input type="number" min="0" max="50" value={form.discount_percent}
                  onChange={e => setForm({ ...form, discount_percent: e.target.value })}
                  data-testid="price-tier-form-discount" />
                <p className="text-[11px] text-[#7A6F5C] mt-1">Entre 0 et 50%</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#E8E0D0] bg-[#FAF7F2]">
              <Button variant="outline" onClick={() => setCreating(false)}>Annuler</Button>
              <Button onClick={create} disabled={busy}
                data-testid="price-tier-form-submit"
                className="bg-[#1F4D3F] hover:bg-[#173B30] text-white">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                Créer le tarif
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceTiersTab;
